import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import {
	getPersonProfileBinaries,
	getProfileBinaryDetail,
	ProfileBinaryDetail,
	ProfileBinaryItem,
	ProfileBinaryResponse,
} from "../../api/person/profile-binary";
import { useGlobalState } from "../globalState";

interface ProfileBinaryHistoryProps {
	personId: number;
}

export const ProfileBinaryHistory = ({ personId }: ProfileBinaryHistoryProps) => {
	const [binaries, setBinaries] = useState<ProfileBinaryItem[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedBinary, setSelectedBinary] = useState<ProfileBinaryDetail | null>(
		null
	);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
	const { currentUser } = useGlobalState();

	useEffect(() => {
		if (!currentUser.is_superuser || !personId) {
			setLoading(false);
			return;
		}

		const fetchProfileBinaries = async () => {
			try {
				setLoading(true);
				const response: ProfileBinaryResponse =
					await getPersonProfileBinaries(personId);
				setBinaries(response.results);
			} catch (err) {
				setError(
					err instanceof Error
						? err.message
						: "Failed to load profile binaries"
				);
			} finally {
				setLoading(false);
			}
		};

		fetchProfileBinaries();
	}, [personId, currentUser.is_superuser]);

	const handleRowClick = async (binaryId: number) => {
		try {
			setLoadingDetail(true);
			setModalOpen(true);
			const detail = await getProfileBinaryDetail(binaryId);
			setSelectedBinary(detail);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to load binary detail"
			);
		} finally {
			setLoadingDetail(false);
		}
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedBinary(null);
	};

	// Don't render anything if user is not superuser
	if (!currentUser.is_superuser) {
		return null;
	}

	if (loading) {
		return (
			<Accordion
				defaultExpanded={false}
				data-component="profile-binary-accordion"
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">Profile Binaries</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<CircularProgress />
					</Box>
				</AccordionDetails>
			</Accordion>
		);
	}

	if (error) {
		return (
			<Accordion
				defaultExpanded={false}
				data-component="profile-binary-accordion"
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">Profile Binaries</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Typography color="error">Error: {error}</Typography>
				</AccordionDetails>
			</Accordion>
		);
	}

	return (
		<>
			<Accordion
				defaultExpanded={false}
				data-component="profile-binary-accordion"
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">
						Profile Binaries ({binaries.length})
					</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					{binaries.length === 0 ? (
						<Typography color="text.secondary">
							No profile binaries found for this person.
						</Typography>
					) : (
						<TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
							<Table size="small" sx={{ minWidth: 600 }}>
								<TableHead>
									<TableRow>
										<TableCell>ID</TableCell>
										<TableCell>Date</TableCell>
										<TableCell>Type</TableCell>
										<TableCell>Extracted</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{binaries.map((item) => (
										<TableRow
											key={item.id}
											sx={{
												"&:hover": {
													backgroundColor: "action.hover",
												},
											}}
											data-component="profile-binary-row"
										>
											<TableCell>
												<Link
													component="button"
													variant="body2"
													color="inherit"
													underline="hover"
													noWrap
													onClick={() =>
														handleRowClick(item.id)
													}
													data-component="profile-binary-open"
												>
													{item.id}
												</Link>
											</TableCell>
											<TableCell>
												<Typography variant="body2" noWrap>
													{format(
														new Date(item.created),
														"MMM dd, yyyy HH:mm"
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography
													variant="body2"
													title={item.profile_type_label}
												>
													{item.profile_type_label}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={
														item.extracted ? "Yes" : "No"
													}
													color={
														item.extracted
															? "success"
															: "default"
													}
													size="small"
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</AccordionDetails>
			</Accordion>

			<Dialog
				open={modalOpen}
				onClose={handleCloseModal}
				maxWidth="lg"
				fullWidth
				data-component="profile-binary-dialog"
			>
				<DialogTitle data-component="dialog-title">
					ProfileJsonBinary Detail
					<IconButton
						aria-label="close"
						onClick={handleCloseModal}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
						}}
						data-component="close-button"
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent data-component="dialog-content">
					{loadingDetail ? (
						<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
							<CircularProgress />
						</Box>
					) : selectedBinary ? (
						<>
							<Box sx={{ mb: 2 }}>
								<Typography
									variant="subtitle2"
									component="span"
									sx={{ mr: 1 }}
								>
									Type:
								</Typography>
								<Typography variant="body2" component="span">
									{selectedBinary.profile_type_label}
								</Typography>
							</Box>

							<Box sx={{ mb: 2 }}>
								<Typography
									variant="subtitle2"
									component="span"
									sx={{ mr: 1 }}
								>
									Date:
								</Typography>
								<Typography variant="body2" component="span">
									{format(
										new Date(selectedBinary.created),
										"MMM dd, yyyy HH:mm:ss"
									)}
								</Typography>
							</Box>

							<Box sx={{ mb: 2 }}>
								<Typography
									variant="subtitle2"
									component="span"
									sx={{ mr: 1 }}
								>
									Extracted:
								</Typography>
								<Chip
									label={selectedBinary.extracted ? "Yes" : "No"}
									color={
										selectedBinary.extracted ? "success" : "default"
									}
									size="small"
								/>
							</Box>

							<Typography variant="subtitle2" gutterBottom>
								JSON Data:
							</Typography>
							<Box
								sx={{
									p: 2,
									bgcolor: "background.default",
									borderRadius: 1,
									maxHeight: "60vh",
									overflow: "auto",
								}}
							>
								<pre
									style={{
										margin: 0,
										whiteSpace: "pre-wrap",
										wordBreak: "break-word",
									}}
								>
									{JSON.stringify(selectedBinary.json_data, null, 2)}
								</pre>
							</Box>
						</>
					) : (
						<Typography>No data available</Typography>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};
